import { useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Icon } from "@/components/ui/Icon";
import { useUpdateWorkspace } from "@/hooks/useUpdateWorkspace";
import { WORKSPACE_PALETTE, WORKSPACE_PALETTE_LABELS, workspaceColor } from "@/lib/colors";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: UUID;
  workspaceName: string;
  currentColor: string | null;
}

export function WorkspaceColorDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  currentColor,
}: Props) {
  const update = useUpdateWorkspace();
  const [pending, setPending] = useState<string | null | "auto">(null);

  // Fallback shown on the "Standard" swatch — what the workspace would look
  // like if the explicit color is cleared.
  const autoColor = workspaceColor(workspaceId);

  async function pick(value: string | null) {
    setPending(value ?? "auto");
    try {
      await update.mutateAsync({
        workspaceId,
        payload: { color: value },
      });
      showToast(t.workspaceColorSaved, "success");
      onOpenChange(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    } finally {
      setPending(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !update.isPending && onOpenChange(o)}
      title={t.workspaceColorTitle.replace("{name}", workspaceName)}
      description={t.workspaceColorDescription}
      width={420}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
          marginTop: 4,
        }}
      >
        {WORKSPACE_PALETTE.map((c) => (
          <Swatch
            key={c}
            color={c}
            label={WORKSPACE_PALETTE_LABELS[c] ?? c}
            selected={currentColor === c}
            busy={pending === c}
            onClick={() => pick(c)}
          />
        ))}
      </div>

      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px solid var(--hairline-soft)",
        }}
      >
        <button
          type="button"
          onClick={() => pick(null)}
          disabled={update.isPending}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "10px 12px",
            background: "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            cursor: update.isPending ? "not-allowed" : "pointer",
            fontSize: 13,
            color: "var(--ink-1)",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            textAlign: "left",
            transition: "background .12s, border-color .12s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: autoColor,
              border: "1px dashed rgba(0,0,0,0.2)",
              flexShrink: 0,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{t.workspaceColorAuto}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{t.workspaceColorAutoHint}</div>
          </div>
          {currentColor === null ? <Icon name="check" size={14} color="var(--ink-2)" /> : null}
        </button>
      </div>
    </Dialog>
  );
}

function Swatch({
  color,
  label,
  selected,
  busy,
  onClick,
}: {
  color: string;
  label: string;
  selected: boolean;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={selected}
      disabled={busy}
      style={{
        position: "relative",
        height: 56,
        borderRadius: 8,
        border: "none",
        background: color,
        cursor: busy ? "wait" : "pointer",
        outline: selected ? "2px solid var(--ink-1)" : "2px solid transparent",
        outlineOffset: 2,
        transition: "outline-color .15s, transform .12s",
        padding: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {selected ? (
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            mixBlendMode: "screen",
          }}
        >
          <Icon name="check" size={18} color="#fff" stroke={2.4} />
        </span>
      ) : null}
    </button>
  );
}
